import type { AgoraRTCErrorCode, AgoraRTCReactError } from 'agora-rtc-react';

export function errorMessageOf(error: unknown): string | undefined {
  const message = (error as { message?: unknown } | null)?.message;

  return typeof message === 'string' ? message : undefined;
}

export function isPermissionDeniedError(error: unknown) {
  const message = errorMessageOf(error) ?? '';

  return (/permission\sdenied/i).test(message) || (/not\sallowed/i).test(message);
}

export function isRateLimitError(error: Error) {
  return (/rate\slimit/i).test(error.message);
}

export function rtcErrorOf(error: AgoraRTCReactError) {
  if (typeof error.rtcError === 'string') {
    return { code: error.rtcError, data: undefined };
  }

  return error.rtcError;
}

export function isCaptureRefused(error: AgoraRTCReactError) {
  return rtcErrorOf(error)?.code === 'PERMISSION_DENIED';
}

// Type-only, so the SDK stays out of the lobby's bundle: a renamed enum member
// fails the build here instead of quietly falling back to the generic message.
const UID_CONFLICT: `${AgoraRTCErrorCode.UID_CONFLICT}` = 'UID_CONFLICT';

export function isUidConflict(error: AgoraRTCReactError) {
  return rtcErrorOf(error)?.code === UID_CONFLICT;
}

const ABSENT_DEVICE = ['NotFoundError', 'OverconstrainedError'];

const ABSENT_DEVICE_CODE = 'DEVICE_NOT_FOUND';

const REFUSED_DEVICE_CODE = 'PERMISSION_DENIED';

export function errorNameOf(error: unknown): string | undefined {
  const name = (error as { name?: unknown } | null)?.name;
  if (typeof name === 'string') {
    return name;
  }

  return undefined;
}

export function errorCodeOf(error: unknown): string | undefined {
  const code = (error as { rtcError?: unknown } | null)?.rtcError ?
    rtcErrorOf(error as AgoraRTCReactError)?.code :
    (error as { code?: unknown } | null)?.code;

  return typeof code === 'string' ? code : undefined;
}

function isAbsentDeviceName(name: string | undefined) {
  return name !== undefined && ABSENT_DEVICE.includes(name);
}

export function isDeviceAbsent(error: unknown) {
  return isAbsentDeviceName(errorNameOf(error)) || errorCodeOf(error) === ABSENT_DEVICE_CODE;
}

export function isBrowserRefusal(error: unknown) {
  return errorNameOf(error) === 'NotAllowedError';
}

export function isCaptureRefusal(error: unknown) {
  return isBrowserRefusal(error) ||
    errorCodeOf(error) === REFUSED_DEVICE_CODE ||
    isPermissionDeniedError(error);
}

export function isMicrophoneRefusal(error: unknown) {
  if (isBrowserRefusal(error)) {
    return true;
  }

  return Boolean((error as { rtcError?: unknown } | null)?.rtcError) && isCaptureRefused(error as AgoraRTCReactError);
}
